<template>
  <div class="whatsapp-signup">
    <ProGate feature="la connexion WhatsApp" />
    <!-- Notification component -->
    <transition name="fade">
      <div v-if="notification.show" 
           class="notification" 
           :class="notification.type">
        <div class="notification-content">
          <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <span>{{ notification.message }}</span>
        </div>
      </div>
    </transition>

    <PageHeader 
      pageName="WhatsApp Business"
      pageTitle="Gestion des Comptes WhatsApp"
      pageSubtitle="Gérez vos numéros officiels WhatsApp Business et associez vos agents."
    />

    <!-- Main Content -->
    <div class="main-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
        <p>Chargement de vos comptes...</p>
      </div>

      <div v-else class="unified-dashboard-area">
        <!-- Liaison suspendue : owner Free dont le quota IA (à vie) est épuisé -> agents coupés -->
        <div v-if="aiSuspended" class="suspended-banner">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          <div class="suspended-text">
            <strong>Liaison suspendue</strong>
            <span>Votre quota IA gratuit est épuisé : vos agents ne répondent plus sur WhatsApp. La configuration reste intacte — passez en Pro pour les réactiver.</span>
          </div>
          <button class="suspended-cta" @click="showUpgradeModal = true">Passer en Pro</button>
        </div>

        <div v-if="accounts.length > 0" class="badge-wrapper">
          <div class="account-count-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="margin-right: 6px; opacity: 0.8;">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>{{ accounts.length }} {{ accounts.length > 1 ? 'comptes' : 'compte' }}</span>
          </div>
        </div>

        <div class="unified-cards-grid">
          <!-- Existing Account Cards -->
          <div v-for="account in accounts" :key="account._id" class="premium-card account-card" :class="{ 'is-suspended': aiSuspended && account.idbot }">
            <!-- Top Visual Section -->
            <div class="card-visual">
              <div class="whatsapp-badge-visual" :class="{ 'agent-mode': account.idbot }">
                <img v-if="account.idbot" :src="`https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${getChatbotName(account)}`" alt="Agent" class="premium-agent-avatar">
                <img v-else :src="whatsappLogoPremium" alt="WhatsApp" class="premium-whatsapp-logo">
              </div>
              <!-- Status Badge Overlay -->
              <div class="status-overlay" :class="{ 'verified': account.code_verification_status === 'VERIFIED' || account.code_verification_status === 'EXPIRED', 'not-verified': account.code_verification_status === 'NOT_VERIFIED' }">
                <svg v-if="account.code_verification_status === 'VERIFIED' || account.code_verification_status === 'EXPIRED'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span class="status-text">{{ (account.code_verification_status === 'VERIFIED' || account.code_verification_status === 'EXPIRED') ? 'Vérifié' : 'Non vérifié' }}</span>
              </div>
              <!-- Mini Action Menu -->
              <div class="mini-menu-container">
                <button class="mini-menu-btn" @click="toggleMenu(account._id)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <circle cx="12" cy="12" r="1" fill="currentColor"/>
                    <circle cx="12" cy="5" r="1" fill="currentColor"/>
                    <circle cx="12" cy="19" r="1" fill="currentColor"/>
                  </svg>
                </button>
                <div v-if="activeMenu === account._id" class="account-menu">
                  <button class="menu-item" @click="goToApiLogs(account._id)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4"/>
                    </svg>
                    <span>Logs API</span>
                  </button>
                  <button v-if="account.idbot" class="menu-item" @click="confirmDissociateChatbot(account)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                    <span>Dissocier</span>
                  </button>
                  <button class="menu-item delete" @click="confirmDelete(account)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Content Section -->
            <div class="card-content">
              <div class="account-titles">
                <h3>{{ account.idbot ? getChatbotName(account) : (account.verified_name || 'WhatsApp Business') }}</h3>
                <p class="phone-number">{{ account.display_phone_number }}</p>
                <span v-if="aiSuspended && account.idbot" class="suspended-pill">⏸ Liaison suspendue</span>
              </div>

              <div class="account-stats-row features-grid">
                <div class="feature-item">
                  <div class="feature-dot" :class="account.quality_rating ? account.quality_rating.toLowerCase() : 'unknown'"></div>
                  <span>Qualité: {{ account.quality_rating || 'UNKNOWN' }}</span>
                </div>
                <div class="feature-item">
                  <div class="feature-dot gray"></div>
                  <span>Type: {{ account.type === 'ecommerce' ? 'E-commerce' : 'Service' }}</span>
                </div>
              </div>

              <!-- Chatbot Section -->
              <div class="chatbot-association-area" :class="{ 'linked': account.idbot }">
                <div v-if="!account.idbot" class="no-chatbot-linked">
                  <span>Aucun chatbot lié</span>
                  <button class="mini-link-btn" @click="openLinkChatbotModal(account)">Lier</button>
                </div>
                <router-link v-else :to="`/dashboard/chatbots/${account.idbot}/home`" class="active-chatbot-link simple">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <span>Gérer l'Agent</span>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Add New Account Card (Now last) -->
          <div class="premium-card add-account-card" :class="{ 'processing': isProcessing }">
            <!-- Top Visual Section -->
            <div class="card-visual">
              <div class="whatsapp-badge-visual">
                <img :src="whatsappLogoPremium" alt="WhatsApp" class="premium-whatsapp-logo">
              </div>
            </div>

            <!-- Content Section -->
            <div class="card-content">
              <div v-if="hasChatbotId" class="chatbot-association-badge">
                <img src="https://api.dicebear.com/9.x/bottts-neutral/svg?seed=Degluma" alt="Agent" class="agent-avatar-mini">
                Auto-link chatbot
              </div>

              <h2>{{ isProcessing ? 'Connexion en cours...' : 'Ajouter un Compte' }}</h2>
              <p class="add-description">
                Connectez un nouveau compte WhatsApp Business en quelques clics.
              </p>

              <div v-if="!isProcessing" class="features-grid">
                <div class="feature-item">
                  <div class="feature-dot"></div>
                  <span>Configuration rapide</span>
                </div>
                <div class="feature-item">
                  <div class="feature-dot"></div>
                  <span>Sécurisé & Officiel</span>
                </div>
              </div>

              <button class="signup-btn premium-btn" @click="launchWhatsAppSignup" :disabled="isProcessing">
                <template v-if="!isProcessing">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  <span>Connecter</span>
                </template>
                <div v-else class="spinner-small"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Process Status Overlay -->
      <transition name="fade">
        <div v-if="showProcessStatus" class="process-overlay">
          <div class="process-modal">
            <div class="process-icon">
              <div class="spinner-large"></div>
            </div>
            <h3>Création du compte en cours</h3>
            <p>Veuillez patienter pendant la configuration de votre compte WhatsApp Business...</p>
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Modal de liaison avec un chatbot -->
    <div v-if="showLinkChatbotModal" class="modal-overlay" @click.self="showLinkChatbotModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Associer un chatbot</h3>
          <button class="close-btn" @click="showLinkChatbotModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div v-if="loadingChatbots" class="loading-chatbots">
            <div class="loading-spinner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <p>Chargement des chatbots...</p>
          </div>
          
          <div v-else-if="chatbots.length === 0" class="no-chatbots">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
            <h4>Aucun chatbot disponible</h4>
            <p>Vous devez d'abord créer un chatbot pour pouvoir l'associer à votre compte WhatsApp.</p>
            <button class="create-chatbot-btn" @click="goToCreateChatbot">
              Créer un chatbot
            </button>
          </div>
          
          <div v-else class="chatbot-selection">
            <p class="selection-info">Sélectionner le chatbot à associer au compte WhatsApp</p>
            
            <div class="chatbots-list">
              <!-- Option pour créer un nouveau chatbot -->
              <div 
                class="chatbot-item create-new"
                @click="goToCreateChatbot"
              >
                <div class="chatbot-item-header">
                  <div class="chatbot-avatar create-avatar">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </div>
                  <div class="chatbot-info">
                    <h4 class="chatbot-name">Créer un nouveau chatbot</h4>
                    <p class="chatbot-phone">Cliquez pour en créer un nouveau</p>
                  </div>
                </div>
              </div>

              <!-- Liste des chatbots existants -->
              <div 
                v-for="bot in chatbots" 
                :key="bot._id || bot.id" 
                class="chatbot-item"
                :class="{ 
                  'selected': selectedChatbot === (bot._id || bot.id),
                  'inactive': bot.status === 'inactive'
                }"
                @click="selectChatbot(bot._id || bot.id)"
              >
                <div class="chatbot-item-header chatbot-box-item">
                  <div class="chatbot-avatar">
                    <img :src="'https://api.dicebear.com/9.x/bottts-neutral/svg?seed=' + (bot.bot_name || 'Degluma')" :alt="bot.bot_name" />
                  </div>
                  <div class="chatbot-info">
                    <h4 class="chatbot-name">{{ bot.bot_name }}</h4>
                    <p class="chatbot-phone">{{ bot.display_phone_number }}</p>
                  </div>
                  <div class="chatbot-status">
                    <span :class="bot.status">{{ bot.status === 'active' ? 'Actif' : 'Inactif' }}</span>
                  </div>
                </div>
                <div class="chatbot-item-body">
                  <p class="chatbot-description">{{ bot.description }}</p>
                  <div class="chatbot-stats">
                    <div class="stat">
                      <span class="stat-label">Utilisateurs</span>
                      <span class="stat-value">{{ bot.userCount }}</span>
                    </div>
                    <div class="stat">
                      <span class="stat-label">Interactions</span>
                      <span class="stat-value">{{ bot.interactionCount }}</span>
                    </div>
                  </div>
                </div>
                <div class="chatbot-select-indicator">
                  <svg v-if="selectedChatbot === bot._id" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="cancel-btn" @click="showLinkChatbotModal = false">Annuler</button>
          <button 
            v-if="chatbots.length > 0" 
            class="link-btn" 
            :disabled="!selectedChatbot"
            @click="linkChatbot"
          >
            Associer
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation de dissociation du chatbot -->
    <div v-if="showDissociateModal" class="modal-overlay" @click.self="showDissociateModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Confirmer la dissociation</h3>
          <button class="close-btn" @click="showDissociateModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="warning-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
            <p>Êtes-vous sûr de vouloir dissocier ce chatbot du compte WhatsApp ? <span class="warning-highlight">Cette action est réversible mais peut affecter vos services.</span></p>
          </div>
          
          <div class="form-group confirmation-form">
            <label>Pour confirmer, tapez <strong>"DISSOCIER"</strong> en majuscules</label>
            <input 
              v-model="dissociateConfirmation" 
              type="text" 
              placeholder="DISSOCIER"
              :class="{ 'error': dissociateConfirmation && dissociateConfirmation !== 'DISSOCIER' }"
            >
            <span v-if="dissociateConfirmation && dissociateConfirmation !== 'DISSOCIER'" class="error-message">
              Veuillez taper exactement "DISSOCIER" en majuscules
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDissociateModal = false">Annuler</button>
          <button 
            class="delete-btn" 
            @click="dissociateChatbot" 
            :disabled="dissociateConfirmation !== 'DISSOCIER'"
          >
            Dissocier
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation de suppression -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="close-btn" @click="showDeleteModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer ce compte WhatsApp ? Cette action est irréversible.</p>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteModal = false">Annuler</button>
          <button class="delete-btn" @click="deleteAccount">Supprimer</button>
        </div>
      </div>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal 
      :show="showUpgradeModal" 
      :current-plan-slug="userSubscription?.slug || null"
      @close="showUpgradeModal = false"
      @selectPlan="handlePlanSelect"
    />
  </div>
</template>

<script>
/* global FB */
import { axiosPeelo } from '@/utils/axios';
import { consoleLog, consoleError } from '@/utils/logger';
import UpgradeModal from '@/components/UpgradeModal.vue';
import PageHeader from '@/components/PageHeader.vue';
import ProGate from '@/components/ProGate.vue';
import whatsappLogoPremium from '@/assets/whatsapp-logo-premium.png';

export default {
  name: 'WhatsAppSignup',
  components: {
    UpgradeModal,
    PageHeader,
    ProGate
  },
  data() {
    return {
      aiSuspended: false, // liaison suspendue : owner Free + quota IA (à vie) épuisé
      loading: true,
      accounts: [],
      isProcessing: false,
      showProcessStatus: false,
      configId: '2117067702537293', // Embedded Signup v4
      metaSignupState: {
        authCode: null,
        whatsappData: null,
        isComplete: false
      },
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      // Association chatbot
      chatbots: [],
      loadingChatbots: false,
      selectedChatbot: null,
      showLinkChatbotModal: false,
      accountToLink: null,
      baseUrlImage: 'https://api.peelo.io',
      // Menu actions
      activeMenu: null,
      showDissociateModal: false,
      accountToDissociate: null,
      dissociateConfirmation: '',
      showDeleteModal: false,
      accountToDelete: null,
      // Pending params from URL
      pendingParams: null,
      // Upgrade Modal
      showUpgradeModal: false,
      whatsappLogoPremium
    };
  },

  computed: {
    // Vérifier si un chatbot est spécifié dans l'URL
    hasChatbotId() {
      return !!this.$route.params.idchatbot;
    },
    chatbotId() {
      return this.$route.params.idchatbot || null;
    },
    // Subscription computed properties
    userSubscription() {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return null;
        const user = JSON.parse(userData);
        return user?.data?.subscription || null;
      } catch (e) {
        return null;
      }
    },
    isFreePlan() {
      return !this.userSubscription;
    }
  },

  methods: {
    // Handle plan selection from upgrade modal
    handlePlanSelect(plan) {
      console.log('Plan selected:', plan);
      this.showUpgradeModal = false;
      
      // For Enterprise, redirect to contact
      if (plan.slug === 'enterprise') {
        window.open('https://wa.me/221781528375?text=Je%20suis%20int%C3%A9ress%C3%A9%20par%20le%20plan%20Enterprise', '_blank');
        return;
      }
      
      // For other plans, redirect to payment page (to be implemented)
      this.showNotificationMessage(`Vous avez choisi le plan ${plan.name}. Redirection vers le paiement...`, 'success');
      // TODO: Implement payment flow
      // this.$router.push(`/dashboard/payment?plan=${plan.slug}`);
    },

    // Récupère le statut du plan : aiSuspended = liaison suspendue (Free + quota IA épuisé).
    async fetchPlanStatus() {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return;
        const token = JSON.parse(userData).data.token;
        const response = await axiosPeelo.get('/strong/billing/my-plan', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        this.aiSuspended = !!(response.data && response.data.ai_suspended);
      } catch (e) {
        this.aiSuspended = false; // silencieux : pas de bannière en cas d'erreur
      }
    },

    // Fetch WhatsApp accounts
    async fetchAccounts(fromUrlParams = false, accountIdToFind = null) {
      this.loading = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const response = await axiosPeelo.get('/strong/whatsapp/accounts', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.accounts = response.data || [];
        
        // Si un accountId est spécifié, trouver le compte correspondant
        if (accountIdToFind && this.accounts.length > 0) {
          this.accountToLink = this.accounts.find(account => account._id === accountIdToFind);
          
          // Si le compte est trouvé et que nous venons des paramètres d'URL, ouvrir le modal
          if (this.accountToLink && fromUrlParams) {
            this.showLinkChatbotModal = true;
            
            // Si les chatbots ne sont pas encore chargés, les charger
            if (this.chatbots.length === 0) {
              const chatbotIdToFind = this.pendingParams?.chatbotIdBi;
              await this.fetchChatbots(fromUrlParams, chatbotIdToFind);
            }
          }
        }
        
        // Si au moins un compte vérifié n'a pas de bot, charger la liste des chatbots
        const needsChatbots = this.accounts.some(
          account => (account.code_verification_status === 'VERIFIED' || account.code_verification_status === 'EXPIRED') && !account.idbot
        );
        
        if (needsChatbots && this.chatbots.length === 0) {
          await this.fetchChatbots();
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
        this.showNotificationMessage(
          err.response?.data?.error || 'Erreur lors du chargement des comptes',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },

    // Format date
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },
    // Facebook SDK Initialization
    initFacebookSDK() {
      consoleLog('🚀 Initialisation du SDK Facebook...');
      
      return new Promise((resolve, reject) => {
        // Vérifier si le SDK est déjà chargé
        if (typeof FB !== 'undefined') {
          consoleLog('✅ Facebook SDK déjà chargé');
          resolve();
          return;
        }
        
        // Timeout pour éviter d'attendre indéfiniment
        const timeout = setTimeout(() => {
          consoleError('❌ TIMEOUT: Le SDK Facebook n\'a pas pu se charger');
          reject(new Error('Facebook SDK loading timeout'));
        }, 15000); // 15 secondes

        (function(d, s, id) {
          var js, fjs = d.getElementsByTagName(s)[0];
          if (d.getElementById(id)) {
            clearTimeout(timeout);
            return;
          }
          js = d.createElement(s);
          js.id = id;
          js.src = "https://connect.facebook.net/en_US/sdk.js";
          js.crossOrigin = "anonymous";
          js.async = true;
          js.defer = true;
          
          js.onerror = function() {
            consoleError('❌ Échec du chargement du script SDK Facebook');
            clearTimeout(timeout);
            reject(new Error('Failed to load Facebook SDK'));
          };
          
          fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));

        window.fbAsyncInit = function() {
          try {
            FB.init({
              appId: '1668577657360308',
              autoLogAppEvents: true,
              xfbml: true,
              version: 'v22.0'
            });
            
            consoleLog('✅ SDK Facebook initialisé avec succès');
            clearTimeout(timeout);
            resolve();
          } catch (error) {
            consoleError('❌ Erreur lors de l\'initialisation du SDK:', error);
            clearTimeout(timeout);
            reject(error);
          }
        };
      });
    },

    // WhatsApp Signup Launch
    async launchWhatsAppSignup() {
      // Check subscription first
      if (this.isFreePlan) {
        console.log('⚠️ User is on Free Plan - showing upgrade modal');
        this.showUpgradeModal = true;
        return;
      }

      console.log('\n\n');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🔄 LANCEMENT DU PROCESSUS D\'INSCRIPTION WHATSAPP');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('⚠️ ALLEZ VOIR LA CONSOLE POUR SUIVRE LE PROCESSUS EN TEMPS RÉEL');
      console.log('═══════════════════════════════════════════════════════════');
      
      // Vérifier si le SDK Facebook est prêt
      if (typeof FB === 'undefined') {
        consoleError('❌ SDK Facebook non disponible !');
        this.showNotificationMessage('SDK Facebook non chargé. Veuillez actualiser la page et réessayer.', 'error');
        return;
      }

      consoleLog('✅ SDK Facebook disponible');
      this.isProcessing = true;

      try {
        // Réinitialiser l'état de synchronisation
        this.metaSignupState = {
          authCode: null,
          whatsappData: null,
          isComplete: false
        };

        // Vérifier l'état de connexion Facebook
        FB.getLoginStatus((statusResponse) => {
          consoleLog('📊 Statut de connexion Facebook:', statusResponse.status);
          
          // Procéder avec le login WhatsApp
          FB.login((response) => {
            consoleLog('📨 Réponse FB.login reçue - Status:', response.status);
            
            if (response.authResponse && response.authResponse.code) {
              console.log('✅ Code d\'authentification reçu');
              
              // Stocker le code d'authentification
              this.metaSignupState.authCode = response.authResponse.code;
              
              this.showNotificationMessage('Connexion réussie ! Création du compte en cours...', 'success');
              this.checkAndCreateAccount();
            } else if (response.error) {
              consoleError('❌ Erreur Facebook Login:', response.error.message || response.error);
              this.showNotificationMessage(`Erreur Facebook: ${response.error.message || response.error}`, 'error');
              this.isProcessing = false;
            } else if (response.status === 'not_authorized') {
              console.warn('⚠️ Connexion non autorisée');
              this.showNotificationMessage('Connexion annulée ou non autorisée', 'error');
              this.isProcessing = false;
            } else {
              consoleError('❌ Échec de la connexion - Status:', response.status);
              this.showNotificationMessage('Échec de la connexion', 'error');
              this.isProcessing = false;
            }
          }, {
            config_id: this.configId,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: 'whatsapp_business_app_onboarding',
              sessionInfoVersion: '3'
            }
          });
        });
      } catch (err) {
        consoleError('❌ Erreur dans launchWhatsAppSignup:', err.message);
        this.showNotificationMessage('Erreur lors du lancement de la configuration WhatsApp', 'error');
        this.isProcessing = false;
      }
    },

    // Event Handlers
    async handleWhatsAppSignupResponse(data) {
      consoleLog('🎯 Message Embedded Signup reçu');

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        consoleLog('📋 Event:', data.event);
        
        if (['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(data.event)) {
          consoleLog('🎉 Inscription réussie !');
          consoleLog('📱 Phone Number ID:', data.data.phone_number_id);
          consoleLog('📱 WABA ID:', data.data.waba_id);
          
          // Stocker les données WhatsApp
          this.metaSignupState.whatsappData = {
            phone_number_id: data.data.phone_number_id,
            waba_id: data.data.waba_id,
            business_id: data.data.business_id
          };
          
          consoleLog('✅ Données stockées, création du compte en cours...');
          this.checkAndCreateAccount();
        } else if (data.event === 'CANCEL') {
          console.warn('⚠️ Processus annulé par l\'utilisateur');
          
          const errorMsg = data.data.error_message
            ? `Erreur: ${data.data.error_message}`
            : `Configuration abandonnée à l'étape: ${data.data.current_step}`;
          this.showNotificationMessage(errorMsg, 'error');
          this.isProcessing = false;
        }
      }
    },

    async checkAndCreateAccount() {
      consoleLog('\n\n');
      consoleLog('╔═══════════════════════════════════════════════════════════╗');
      consoleLog('║  🔍 VÉRIFICATION ET CRÉATION DU COMPTE                   ║');
      consoleLog('╚═══════════════════════════════════════════════════════════╝');
      consoleLog('═══════════════════════════════════════════════════════════');
      consoleLog('📊 État actuel de metaSignupState:');
      consoleLog(JSON.stringify(this.metaSignupState, null, 2));
      consoleLog('═══════════════════════════════════════════════════════════');
      
      // Vérifier si nous avons toutes les données nécessaires
      consoleLog('🔍 Vérification des prérequis:');
      consoleLog('  - AuthCode présent:', !!this.metaSignupState.authCode);
      consoleLog('  - WhatsappData présent:', !!this.metaSignupState.whatsappData);
      consoleLog('  - IsComplete:', this.metaSignupState.isComplete);
      
      if (this.metaSignupState.authCode && this.metaSignupState.whatsappData && !this.metaSignupState.isComplete) {
        consoleLog('✅ Tous les prérequis sont satisfaits !');
        consoleLog('═══════════════════════════════════════════════════════════');
        
        try {
          // Marquer comme complet pour éviter les doublons
          consoleLog('🔒 Marquage comme complet pour éviter les doublons...');
          this.metaSignupState.isComplete = true;
          this.showProcessStatus = true;

          // Préparer les données du compte
          const accountData = {
            type: 'customer_support', // Type par défaut
            config_id: this.configId,
            all: this.metaSignupState,
            auth_code: this.metaSignupState.authCode,
            phone_number_id: this.metaSignupState.whatsappData.phone_number_id,
            waba_id: this.metaSignupState.whatsappData.waba_id,
            business_id: this.metaSignupState.whatsappData.business_id,
            code_verification_status: 'VERIFIED'
          };

          // Ajouter l'idchatbot si présent dans les paramètres de route
          const idchatbot = this.$route.params.idchatbot;
          if (idchatbot) {
            accountData.idbot = idchatbot;
            consoleLog('📌 Chatbot ID détecté depuis l\'URL:', idchatbot);
            consoleLog('   → Le compte sera automatiquement associé à ce chatbot');
          }

          consoleLog('\n📦 DONNÉES DU COMPTE À ENVOYER À L\'API:');
          consoleLog('═══════════════════════════════════════════════════════════');
          consoleLog(JSON.stringify(accountData, null, 2));
          consoleLog('═══════════════════════════════════════════════════════════');
          
          consoleLog('🔑 Récupération du token utilisateur...');
          const userData = localStorage.getItem('user');
          if (!userData) {
            throw new Error('Données utilisateur non trouvées');
          }
          
          const userObj = JSON.parse(userData);
          const token = userObj.data.token;
          consoleLog('✅ Token récupéré:', token ? `${token.substring(0, 20)}...` : 'N/A');

          consoleLog('\n🚀 ENVOI DE LA REQUÊTE API...');
          consoleLog('Endpoint: POST /strong/create/whatsapp/account/test');
          consoleLog('═══════════════════════════════════════════════════════════');
          
          const response = await axiosPeelo.post('/strong/create/whatsapp/account/test', accountData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          consoleLog('\n✅ RÉPONSE DE L\'API REÇUE:');
          consoleLog('═══════════════════════════════════════════════════════════');
          consoleLog('Status:', response.status);
          consoleLog('Status Text:', response.statusText);
          consoleLog('Data:', JSON.stringify(response.data, null, 2));
          consoleLog('═══════════════════════════════════════════════════════════');

          consoleLog('🎉 COMPTE CRÉÉ AVEC SUCCÈS !');
          consoleLog('═══════════════════════════════════════════════════════════');
          
          this.showNotificationMessage('Compte WhatsApp créé avec succès !', 'success');
          
          // Recharger la liste des comptes
          await this.fetchAccounts();
          
          // Masquer le statut du processus
          this.showProcessStatus = false;
          this.isProcessing = false;

        } catch (err) {
          consoleError('\n\n❌ ERREUR LORS DE LA CRÉATION DU COMPTE:');
          consoleError('═══════════════════════════════════════════════════════════');
          consoleError('Type:', err.name);
          consoleError('Message:', err.message);
          
          if (err.response) {
            consoleError('\n📥 Réponse d\'erreur de l\'API:');
            consoleError('  - Status:', err.response.status);
            consoleError('  - Status Text:', err.response.statusText);
            consoleError('  - Data:', JSON.stringify(err.response.data, null, 2));
            consoleError('  - Headers:', JSON.stringify(err.response.headers, null, 2));
          } else if (err.request) {
            consoleError('\n📤 Requête envoyée mais pas de réponse:');
            consoleError(err.request);
          } else {
            consoleError('\n⚠️ Erreur lors de la configuration de la requête:');
            consoleError(err.message);
          }
          
          if (err.stack) {
            consoleError('\n📚 Stack trace:');
            consoleError(err.stack);
          }
          consoleError('═══════════════════════════════════════════════════════════');
          
          this.showNotificationMessage(
            err.response?.data?.error || 'Erreur lors de la création du compte WhatsApp',
            'error'
          );
          // Réinitialiser l'état en cas d'erreur
          this.metaSignupState.isComplete = false;
          this.isProcessing = false;
          this.showProcessStatus = false;
        }
      } else {
        consoleWarn('\n⚠️ PRÉREQUIS NON SATISFAITS:');
        consoleWarn('═══════════════════════════════════════════════════════════');
        consoleWarn('Détails:');
        consoleWarn('  - AuthCode présent:', !!this.metaSignupState.authCode);
        consoleWarn('  - WhatsappData présent:', !!this.metaSignupState.whatsappData);
        consoleWarn('  - IsComplete:', this.metaSignupState.isComplete);
        consoleWarn('\n❌ Impossible de créer le compte sans toutes les données');
        consoleWarn('💡 Attendez que les données soient collectées par l\'Embedded Signup');
        consoleWarn('═══════════════════════════════════════════════════════════');
      }
    },

    showNotificationMessage(message, type = 'success') {
      // Clear any existing timeout
      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }

      // Set notification
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 5000)
      };
    },

    // Gestion du menu
    toggleMenu(accountId) {
      this.activeMenu = this.activeMenu === accountId ? null : accountId;
    },

    // Charger les chatbots
    async fetchChatbots(fromUrlParams = false, chatbotIdToFind = null) {
      this.loadingChatbots = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const response = await axiosPeelo.get('/get/list/chatbots', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.chatbots) {
          this.chatbots = response.data.chatbots;
          
          // Si un chatbotId est spécifié, trouver le chatbot correspondant
          if (chatbotIdToFind && this.chatbots.length > 0) {
            const chatbotToLink = this.chatbots.find(chatbot => chatbot._id === chatbotIdToFind);
            
            // Si le chatbot est trouvé et que nous avons déjà un compte sélectionné, préparer l'association
            if (chatbotToLink && this.accountToLink && fromUrlParams) {
              this.selectedChatbot = chatbotToLink._id;
            }
          }
        }
      } catch (error) {
        consoleError('Error fetching chatbots:', error);
        this.showNotificationMessage(
          error.response?.data?.error || 'Erreur lors du chargement des chatbots',
          'error'
        );
      } finally {
        this.loadingChatbots = false;
      }
    },

    // Ouvrir le modal de liaison
    openLinkChatbotModal(account) {
      this.accountToLink = account;
      this.showLinkChatbotModal = true;
      this.activeMenu = null;
      if (this.chatbots.length === 0) {
        this.fetchChatbots();
      }
    },

    // Sélectionner un chatbot
    selectChatbot(botId) {
      this.selectedChatbot = botId;
    },

    // Lier un chatbot
    async linkChatbot() {
      if (!this.selectedChatbot || !this.accountToLink) {
        this.showNotificationMessage('Veuillez sélectionner un chatbot', 'error');
        return;
      }

      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        const response = await axiosPeelo.post('/strong/setup/association/chatbot', {
          idwhatsapp: this.accountToLink._id,
          idbot: this.selectedChatbot
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && (response.data.message === "Association réussie" || response.data.success)) {
          this.showNotificationMessage('Chatbot associé avec succès', 'success');
          await this.fetchAccounts();
          this.showLinkChatbotModal = false;
          this.selectedChatbot = null;
          this.accountToLink = null;
        }
      } catch (err) {
        console.error('Error linking chatbot:', err);
        this.showNotificationMessage(
          err.response?.data?.error || 'Erreur lors de la liaison du chatbot',
          'error'
        );
      }
    },

    // Obtenir le nom du chatbot
    getChatbotName(account) {
      if (account && account.idbot && account.bot_name) {
        return account.bot_name;
      }
      return 'Chatbot non trouvé';
    },

    // Confirmer la dissociation
    confirmDissociateChatbot(account) {
      this.accountToDissociate = account;
      this.showDissociateModal = true;
      this.dissociateConfirmation = '';
      this.activeMenu = null;
    },

    // Dissocier un chatbot
    async dissociateChatbot() {
      if (!this.accountToDissociate || !this.accountToDissociate.idbot) return;
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const response = await axiosPeelo.post('/strong/setup/dissociation/chatbot', {
          idwhatsapp: this.accountToDissociate._id,
          idbot: this.accountToDissociate.idbot
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && (response.data.message === 'Dissociation réussie' || response.data.success)) {
          this.showNotificationMessage('Chatbot dissocié avec succès', 'success');
          this.showDissociateModal = false;
          this.dissociateConfirmation = '';
          this.accountToDissociate = null;
          await this.fetchAccounts();
        }
      } catch (err) {
        console.error('Erreur lors de la dissociation du chatbot:', err);
        this.showNotificationMessage(
          err.response?.data?.error || 'Erreur lors de la dissociation du chatbot',
          'error'
        );
      }
    },

    // Confirmer la suppression
    confirmDelete(account) {
      this.accountToDelete = account;
      this.showDeleteModal = true;
      this.activeMenu = null;
    },

    // Supprimer un compte
    async deleteAccount() {
      if (!this.accountToDelete) return;
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;

        await axiosPeelo.delete(`/strong/whatsapp/accounts/${this.accountToDelete._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        this.accounts = this.accounts.filter(acc => acc._id !== this.accountToDelete._id);
        this.showDeleteModal = false;
        this.accountToDelete = null;
        this.activeMenu = null;
        
        this.showNotificationMessage('Compte WhatsApp supprimé avec succès', 'success');
      } catch (err) {
        console.error('Error deleting account:', err);
        this.showNotificationMessage(
          err.response?.data?.error || 'Erreur lors de la suppression du compte WhatsApp',
          'error'
        );
      }
    },

    // Naviguer vers la création de chatbot
    goToCreateChatbot() {
      this.showLinkChatbotModal = false;
      const query = { 
        returnTo: 'whatsapp_signup', 
        openpopupwhatsapp: 'true'
      };
      
      // Ajouter accountId seulement s'il existe
      if (this.accountToLink?._id) {
        query.accountId = this.accountToLink._id;
      }
      
      this.$router.push({
        path: '/dashboard/chatbots/create',
        query
      });
    },

    // Naviguer vers les logs API
    goToApiLogs(accountId) {
      this.activeMenu = null;
      this.$router.push({
        path: `/dashboard/whatsapp/logs/${accountId}`
      });
    },
  },

  async mounted() {
    console.log('🚀 WhatsAppSignup - Composant monté');
    
    // Charger les comptes existants immédiatement
    await this.fetchAccounts();

    // Statut de liaison (bannière "Liaison suspendue" si Free + quota IA épuisé)
    this.fetchPlanStatus();
    
    // Vérifier si le paramètre openpopupwhatsapp est présent dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const openPopup = urlParams.get('openpopupwhatsapp');
    const accountIdBi = urlParams.get('accountId');
    const chatbotIdBi = urlParams.get('chatbotId');
    
    // Stocker les paramètres pour les utiliser après le chargement des données si nécessaire
    this.pendingParams = {
      openPopup,
      accountIdBi,
      chatbotIdBi
    };
    
    if (openPopup === 'true') {
      this.showLinkChatbotModal = true;
      
      // Si les comptes sont déjà chargés, trouver le compte correspondant
      if (accountIdBi && this.accounts.length > 0) {
        this.accountToLink = this.accounts.find(account => account._id === accountIdBi);
      } 
      // Sinon, forcer le chargement des comptes
      else if (accountIdBi) {
        await this.fetchAccounts(true, accountIdBi);
      }
      
      // Si les chatbots sont déjà chargés, trouver le chatbot correspondant
      if (chatbotIdBi && this.chatbots.length > 0) {
        const chatbotToLink = this.chatbots.find(chatbot => chatbot._id === chatbotIdBi);
        if (chatbotToLink) {
          this.selectedChatbot = chatbotToLink._id;
        }
      }
      // Sinon, forcer le chargement des chatbots
      else if (chatbotIdBi) {
        await this.fetchChatbots(true, chatbotIdBi);
      }
    }
    
    // Initialiser le SDK Facebook
    this.initFacebookSDK()
      .then(() => {
        // Setup WhatsApp signup listener
        window.addEventListener('message', (event) => {
          // Ignorer silencieusement les messages qui ne viennent pas de Facebook
          if (!event.origin.endsWith('facebook.com')) {
            return;
          }
          
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'WA_EMBEDDED_SIGNUP') {
              console.log('🎯 Message WA_EMBEDDED_SIGNUP détecté');
              this.handleWhatsAppSignupResponse(data);
            }
          } catch (error) {
            // Ignorer les erreurs de parsing silencieusement
          }
        });
        
        console.log('✅ Listener installé - Prêt pour l\'inscription');
      })
      .catch(err => {
        console.error('❌ Erreur lors de l\'initialisation du SDK Facebook:', err.message);
        this.showNotificationMessage(
          'Erreur lors de l\'initialisation du SDK Facebook. Veuillez actualiser la page.',
          'error'
        );
      });
  }
};
</script>

<style scoped>
.whatsapp-signup {
  padding: 24px;
}


.main-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #F9FAFB;
  border-radius: 12px;
  margin: 20px 0;
  color: #6F767E;
}

.loading-state .loading-spinner {
  color: #1EA355;
  margin-bottom: 16px;
}

.loading-state p {
  margin-top: 16px;
  font-size: 16px;
}

/* Unified Dashboard Area */
.unified-dashboard-area {
  max-width: 1200px;
  margin: 0 auto;
}

.badge-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
  width: 100%;
}

.account-count-badge {
  display: flex;
  align-items: center;
  background: #DAFFD1;
  color: #143028;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(218, 255, 209, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Unified Cards Grid */
.unified-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  align-items: stretch;
}

.account-card {
  position: relative;
}

/* Status Overlay */
.status-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 10px;
  background: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  font-size: 11px;
  font-weight: 700;
  z-index: 2;
}

.status-overlay.verified {
  color: #1EA355;
  border: 1px solid rgba(30, 163, 85, 0.2);
}

.status-overlay.not-verified {
  color: #DC2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.status-text {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Mini Action Menu */
.mini-menu-container {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.mini-menu-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border: 1px solid #E8ECEF;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #143028;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mini-menu-btn:hover {
  background: white;
  border-color: #DAFFD1;
  color: #1EA355;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.account-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 14px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 6px;
  min-width: 140px;
  z-index: 100;
  animation: slideIn 0.2s ease;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.account-menu .menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #4B5563;
  cursor: pointer;
  transition: all 0.2s ease;
}

.account-menu .menu-item:hover {
  background: #F3F4F6;
  color: #143028;
}

.account-menu .menu-item.delete {
  color: #DC2626;
}

.account-menu .menu-item.delete:hover {
  background: #FEF2F2;
}

/* Account Info & Stats */
.account-titles {
  margin-bottom: 16px;
  width: 100%;
  text-align: center;
}

.account-titles h3 {
  font-size: 18px;
  font-weight: 700;
  color: #143028;
  margin: 0 0 4px 0;
}

.phone-number {
  font-size: 14px;
  color: #6F767E;
  margin: 0;
}

/* Chatbot Association Area */
.chatbot-association-area {
  width: 100%;
  padding: 12px 20px;
  background: #143028 !important;
  color: white !important;
  border: none;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chatbot-association-area.linked {
  background: transparent !important;
  color: #143028 !important;
  border: 1px solid rgba(20, 48, 40, 0.1);
  padding: 8px 16px;
}

.chatbot-association-area.linked:hover {
  background: #F9FAFB !important;
  border-color: #DAFFD1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.chatbot-association-area:hover {
  background: #1E463A !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.2);
}

.no-chatbot-linked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 13px;
  color: white !important;
  font-weight: 700;
}

.mini-link-btn {
  padding: 4px 12px;
  background: white;
  color: #143028;
  border: none;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mini-link-btn:hover {
  background: #DAFFD1;
}

.active-chatbot-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white !important;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

.active-chatbot-link.simple {
  color: #143028 !important;
  font-size: 13px;
  font-weight: 700;
}

.active-chatbot-link:hover {
  opacity: 0.9;
}

.agent-avatar-mini {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  object-fit: contain;
  flex-shrink: 0;
}

/* Premium Card Base */
.premium-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #E8ECEF;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 360px;
}

.premium-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  border-color: #DAFFD1;
}

.card-visual {
  background: #143028; /* Peelo Dark Green */
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.whatsapp-badge-visual {
  width: 90px;
  height: 90px;
  background: white;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.4s ease;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.whatsapp-badge-visual.agent-mode {
  border-color: #DAFFD1;
  background: transparent;
}

.premium-whatsapp-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.premium-agent-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.premium-card:hover .whatsapp-badge-visual {
  transform: scale(1.1) rotate(-5deg);
}

.card-content {
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chatbot-association-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: rgba(20, 48, 40, 0.05);
  color: #143028;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 16px;
}

.premium-card h2 {
  font-size: 20px;
  font-weight: 700;
  color: #143028;
  margin: 0 0 10px 0;
}

.premium-card .add-description {
  color: #6F767E;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 20px;
}

.features-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
  width: 100%;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #4B5563;
  padding: 6px 12px;
  background: #F9FAFB;
  border-radius: 10px;
}

.feature-item span {
  font-weight: 600;
}

.feature-dot {
  width: 8px;
  height: 8px;
  background: #143028;
  border-radius: 50%;
  flex-shrink: 0;
}

.feature-dot.green { background: #1EA355; }
.feature-dot.yellow { background: #F59E0B; }
.feature-dot.red { background: #DC2626; }
.feature-dot.gray { background: #6B7280; }
.feature-dot.unknown { background: #9CA3AF; }

.premium-btn {
  width: 100%;
  padding: 12px 24px;
  background: #143028 !important; /* Peelo Dark Green */
  color: white !important;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.premium-btn:hover:not(:disabled) {
  background: #1E463A !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.2);
}

.premium-btn:disabled {
  background: #9CA3AF !important;
  cursor: not-allowed;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.console-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: #F9FAFB;
  border-radius: 6px;
  font-size: 12px;
  color: #6F767E;
}

.console-hint svg {
  width: 14px;
  height: 14px;
}

/* Spinners */
.spinner-large {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(30, 163, 85, 0.2);
  border-top-color: #1EA355;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Process Overlay */
.process-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.process-modal {
  background: white;
  border-radius: 16px;
  padding: 48px;
  max-width: 400px;
  text-align: center;
  border: 1px solid #E5E7EB;
}

.process-icon {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.process-modal h3 {
  font-size: 24px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 12px 0;
}

.process-modal p {
  color: #6F767E;
  font-size: 16px;
  margin: 0 0 24px 0;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #E8ECEF;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  width: 100%;
  height: 100%;
  background: #1EA355;
  animation: progress 1.5s ease-in-out infinite;
}

@keyframes progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  max-width: 400px;
}

.notification.success {
  background-color: #EBF7EE;
  color: #1EA355;
  border: 1px solid #D1E7DD;
}

.notification.error {
  background-color: #FEF2F2;
  color: #DC2626;
  border: 1px solid #FCA5A5;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-content svg {
  flex-shrink: 0;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Header Actions and Menu */
.account-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.account-menu-container {
  position: relative;
}

.menu-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.account-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 100;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
  background: none;
  border: none;
  color: #1A1D1F;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background: #E8ECEF;
}

.menu-item.delete {
  color: #FF3B30;
}

.menu-item.delete:hover {
  background: #FFD5D5;
}

.menu-item svg {
  flex-shrink: 0;
}

/* Chatbot Section */
.chatbot-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E8ECEF;
}

.chatbot-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #1EA355;
}

.chatbot-label {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chatbot-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.no-chatbot {
  color: #6F767E;
  font-size: 14px;
  font-style: italic;
}

.link-chatbot-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #1EA355;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.link-chatbot-btn:hover {
  background: #167F42;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(30, 163, 85, 0.2);
}

.chatbot-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #E6F7EE;
  color: #1EA355;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  transition: all 0.2s ease;
}

.chatbot-tag svg {
  color: #1EA355;
}

.chatbot-tag.clickable {
  cursor: pointer;
  padding-right: 10px;
}

.chatbot-tag.clickable:hover {
  background-color: #D1F0E0;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(30, 163, 85, 0.1);
}

.chatbot-tag .arrow-icon {
  margin-left: 4px;
  opacity: 0.7;
  transition: transform 0.2s ease;
}

.chatbot-tag.clickable:hover .arrow-icon {
  transform: translateX(2px);
  opacity: 1;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #E8ECEF;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #E8ECEF;
}

.cancel-btn {
  padding: 10px 20px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  color: #1A1D1F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #F5F5F5;
}

.link-btn {
  padding: 10px 20px;
  background: #1EA355;
  border: none;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.link-btn:hover:not(:disabled) {
  background: #167F42;
}

.link-btn:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
  opacity: 0.6;
}

.delete-btn {
  padding: 10px 20px;
  background: #FF3B30;
  border: none;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover:not(:disabled) {
  background: #E02D1E;
}

.delete-btn:disabled {
  background: #FF9A94;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Modal Content Styles */
.loading-chatbots {
  text-align: center;
  padding: 32px 0;
}

.loading-chatbots .loading-spinner {
  margin-bottom: 16px;
}

.no-chatbots {
  text-align: center;
  padding: 32px 0;
}

.no-chatbots svg {
  color: #6B7280;
  margin-bottom: 16px;
}

.no-chatbots h4 {
  font-size: 18px;
  color: #1F2937;
  margin: 0 0 8px 0;
}

.no-chatbots p {
  color: #6B7280;
  margin-bottom: 24px;
}

.create-chatbot-btn {
  padding: 8px 24px;
  background-color: #1EA355;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-chatbot-btn:hover {
  background-color: #167F42;
}

.chatbot-selection {
  padding: 16px 0;
}

.selection-info {
  margin-bottom: 14px;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
}

.chatbots-list {
  max-height: 400px;
  overflow-y: auto;
  margin: 20px 0;
  padding-right: 8px;
}

.chatbots-list::-webkit-scrollbar {
  width: 8px;
}

.chatbots-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.chatbots-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chatbot-item {
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;
}

.chatbot-item:hover {
  border-color: #1EA355;
  box-shadow: 0 2px 8px rgba(30, 163, 85, 0.1);
}

.chatbot-item.selected {
  border-color: #1EA355;
  background: #F0FFF7;
  box-shadow: 0 2px 8px rgba(30, 163, 85, 0.1);
}

.chatbot-item.create-new {
  background: #F9FAFB;
  border: 2px dashed #1EA355;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chatbot-item.create-new:hover {
  border-color: #167F42;
  background: #E6F7EE;
}

.chatbot-item.create-new .chatbot-avatar {
  background: white;
  color: #1EA355;
  border: 2px solid #1EA355;
}

.chatbot-item.create-new .chatbot-name {
  color: #1EA355;
  font-size: 16px;
  font-weight: 600;
}

.chatbot-item-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.chatbot-box-item {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #E8ECEF;
}

.chatbot-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.create-avatar {
  background: white;
  color: #1EA355;
  border: 2px solid #1EA355;
}

.chatbot-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chatbot-avatar svg {
  color: #6F767E;
}

.chatbot-info {
  flex: 1;
  min-width: 0;
}

.chatbot-name {
  font-size: 16px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-phone {
  font-size: 13px;
  color: #6F767E;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-status {
  margin-left: auto;
  flex-shrink: 0;
}

.chatbot-status span {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
}

.chatbot-status .active {
  background-color: #E6F7EE;
  color: #1EA355;
}

.chatbot-status .inactive {
  background-color: #F5F5F5;
  color: #6F767E;
}

.chatbot-item-body {
  margin-top: 8px;
}

.chatbot-description {
  font-size: 14px;
  color: #6F767E;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chatbot-stats {
  display: flex;
  gap: 24px;
  margin-top: 12px;
}

.stat {
  display: inline-block;
  margin-right: 24px;
  text-align: center;
  min-width: 80px;
}

.stat-label {
  font-size: 12px;
  color: #6B7280;
  display: block;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
  display: block;
  text-align: center;
}

.chatbot-select-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  color: #1EA355;
}

/* Warning Message */
.warning-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #FFF5F5;
  border-radius: 12px;
  margin-bottom: 24px;
  border-left: 4px solid #FF3B30;
}

.warning-message svg {
  color: #FF3B30;
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-message p {
  margin: 0;
  color: #1A1D1F;
  font-size: 15px;
  line-height: 1.6;
}

.warning-highlight {
  font-weight: 600;
  color: #FF3B30;
}

/* Form Group */
.form-group {
  margin-bottom: 16px;
}

.confirmation-form {
  background: #F9FAFB;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #E8ECEF;
}

.confirmation-form label {
  display: block;
  margin-bottom: 12px;
  font-size: 15px;
  color: #1A1D1F;
  font-weight: 500;
}

.confirmation-form strong {
  color: #FF3B30;
}

.confirmation-form input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #E8ECEF;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.confirmation-form input:focus {
  outline: none;
  border-color: #1EA355;
  box-shadow: 0 0 0 3px rgba(30, 163, 85, 0.1);
}

.confirmation-form input.error {
  border-color: #FF3B30;
}

.error-message {
  display: block;
  margin-top: 8px;
  color: #FF3B30;
  font-size: 13px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .whatsapp-signup {
    padding: 16px;
  }

  .header-title h1 {
    font-size: 32px;
  }

  .unified-cards-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .premium-card {
    max-width: 100%;
  }
}

/* ===== Liaison suspendue (Free + quota IA épuisé) ===== */
.suspended-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #FFF4E5;
  border: 1px solid #F2C28B;
  color: #7A4A12;
  border-radius: 14px;
  padding: 14px 18px;
  margin: 0 0 20px;
}
.suspended-banner > svg { flex-shrink: 0; color: #C97A1E; }
.suspended-text { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.suspended-text strong { font-size: 0.95rem; }
.suspended-text span { font-size: 0.82rem; opacity: 0.85; line-height: 1.4; }
.suspended-cta {
  flex-shrink: 0;
  background: #143028; color: #fff; border: none; cursor: pointer;
  padding: 0.6rem 1.1rem; border-radius: 9999px;
  font-size: 0.8rem; font-weight: 800; white-space: nowrap;
  transition: transform 0.2s;
}
.suspended-cta:hover { transform: scale(1.03); }

.suspended-pill {
  display: inline-block; margin-top: 6px;
  background: #FDE2E1; color: #B42318;
  font-size: 0.7rem; font-weight: 800;
  padding: 3px 10px; border-radius: 9999px;
}
.account-card.is-suspended { opacity: 0.72; }
.account-card.is-suspended .whatsapp-badge-visual { filter: grayscale(0.6); }

@media (max-width: 640px) {
  .suspended-banner { flex-direction: column; align-items: flex-start; }
  .suspended-cta { width: 100%; }
}
</style>

